import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { Button, ButtonShapes } from '@va/ui/design-system';
import { useTooltipContext } from '@va/ui/tooltips';
import { findFirstFocusableElement, useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { CalendarPanel, Shortcut } from './components';
import { useDatePickerContext } from './context';

export const DatePickerTooltip = () => {
  const translate = useTranslate();
  const tooltip = useTooltipContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const { shortcuts, autoApply, calendar, panelState, isDirty, applyDate, resetPanelState } = useDatePickerContext();

  useIsolatedFocusedContainer({
    containerRef,
    enabled: tooltip.context?.open,
    dependencies: [calendar, panelState],
    focusFirstElement: false,
  });

  useEffect(() => {
    if (!containerRef.current || !tooltip.context?.open) return;

    const firstFocusable = findFirstFocusableElement(containerRef.current);
    firstFocusable?.focus();
  }, [tooltip.context?.open]);

  const hide = useCallback(() => {
    tooltip.context.onOpenChange(false);
    resetPanelState();
  }, [resetPanelState, tooltip.context]);

  return (
    <div
      ref={containerRef}
      date-testid={TEST_IDS.specific.datePicker.dropdownContainer}
      className={
        '!text-gray-charcoal w-full bg-white border rounded-15 border-black/10 h-full sm-initial:overflow-y-auto max-h-full sm-initial:max-h-[100svh]'
      }
    >
      <div className='shortcut-calendar-container flex flex-1 flex-wrap justify-center p-3'>
        {shortcuts && <Shortcut />}
        <div className='picker-wrapper relative flex flex-wrap p-1'>
          <div className='divider-wrapper hidden absolute inset-0 justify-center items-center'>
            <div className='divider-row w-8 h-1 bg-transparent' />
          </div>
          <CalendarPanel
            calendar={calendar.previous}
            panelState={panelState.previous}
            data-testid={TEST_IDS.generic.datePicker.leftPanel}
          />
          <CalendarPanel
            calendar={calendar.next}
            panelState={panelState.next}
            data-testid={TEST_IDS.generic.datePicker.rightPanel}
          />
        </div>
      </div>

      {!autoApply ? (
        <div className={classNames('bg-white z-10 sticky bottom-0')}>
          <div className='mx-1 py-2 border-t border-black/10 dark:border-primary-700'>
            <div className='flex gap-2 justify-around items-center'>
              <Button
                onClick={hide}
                text={translate('card.button.cancel')}
                shape={ButtonShapes.circle}
                color='secondary'
                data-testid={TEST_IDS.generic.buttons.cancel}
                className='border-primary text-primary border-2 active:focus:ring-white !bg-white'
              />
              <Button
                text={translate('card.button.apply')}
                disabled={!isDirty}
                data-testid={TEST_IDS.generic.buttons.apply}
                onClick={() => {
                  applyDate();
                  hide();
                }}
                shape={ButtonShapes.circle}
                className='min-w-[6.25rem]'
              />
            </div>
          </div>
        </div>
      ) : (
        <div className='hidden-footer sticky bottom-0 bg-white z-10'>
          <div className='mx-2 py-2 border-t border-black/10 dark:border-primary-700 flex justify-end'>
            <div className='footer-wrapper'>
              <Button
                onClick={hide}
                text={translate('card.button.cancel')}
                shape={ButtonShapes.circle}
                color='secondary'
                data-testid={TEST_IDS.generic.buttons.cancel}
                className='border-primary text-primary border-2 active:focus:ring-white !bg-white'
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
